import {createAsyncThunk} from '@reduxjs/toolkit'

import {SchemasOrderFamilyPay} from '@shared/schemas/order'

import {changeLocation, systemAlert} from '@web/actions/application'
import {navigateToOrderDetail} from '@web/actions/navigateTo'
import {setMaximumRechargePoint} from '@web/actions/paymentMethod'
import {usingAllNaverPoint} from '@web/actions/pointPayment'
import {PayMethodType} from '@web/core/constants'
import {ROUTER_ORDER_SHEET_FAMILY_PAY} from '@web/core/routes'
import getApi, {createApiAsyncThunk, createWrapper} from '@web/helpers/api'
import {rechargePaymentInfoSelector} from '@web/selectors/paymentMethodInfo'
import {addNaverId} from '@web/slices/abTest/slice'
import {commonSelector} from '@web/slices/common/slice'
import {orderSheetProductInfoSelector} from '@web/slices/orderSheetProducts/slice'
import {getAvailablePayMethodTypes} from '@web/slices/paymentMethod/slice'
import {naverPayPointSelector} from '@web/slices/pointPayment/slice'

import {FamilyPayOrderSheetResponse, OrderSheetError} from '../apis/response'
import {ApiBaseUrl, ApiUrlInfo} from '../constants'
import {CheckoutThunkApi as ThunkApi} from '../interfaces'

const api = getApi(ApiBaseUrl)

export type InitOrderSheetByAPIPayloadType = {
    data: FamilyPayOrderSheetResponse
    availablePayMethodTypes: PayMethodType[]
}

/** 패밀리 맴버십 조회 action */
export const initOrderSheetByAPI = createApiAsyncThunk<
    InitOrderSheetByAPIPayloadType,
    string,
    ThunkApi<OrderSheetError>
>('familyPay/initOrderSheetByAPI', async (orderNo) => {
    const response = await createWrapper<SchemasOrderFamilyPay['/']['response']>(
        api.get(ApiUrlInfo.orderSheet, {
            params: {orderNo, userAgent: navigator.userAgent},
        }),
    )

    const result: InitOrderSheetByAPIPayloadType = {
        // type fix 필요
        data: response as FamilyPayOrderSheetResponse,
        availablePayMethodTypes: getAvailablePayMethodTypes({
            availablePayMethodTypes: response.payMethod.availablePayMethodTypes as PayMethodType[],
        }),
    }

    return result
})

/** 패밀리 맴버십 초기화 action */
export const initOrderSheet = createAsyncThunk<
    {
        isApiSuccess: boolean
    },
    string,
    ThunkApi
>('familyPay/initOrderSheet', async (orderNo, {dispatch, getState}) => {
    const {data} = await dispatch(initOrderSheetByAPI(orderNo)).unwrap()
    // 데이터가 없다면 주문서 조회 실패
    const isApiSuccess = !!data

    if (isApiSuccess) {
        if (naverPayPointSelector(getState()).usePointAll) {
            // 데이터 반영후 포인트 최대화 계산
            await dispatch(usingAllNaverPoint())
        } else if (rechargePaymentInfoSelector(getState()).isNeedRecharging) {
            // 데이터 반영후 충전포인트 최대화 계산
            await dispatch(setMaximumRechargePoint())
        }
    }

    dispatch(addNaverId(data.purchaser.naverId))

    return {
        isApiSuccess,
    }
})

/** 패밀리 결제 결과 */
export const initResult = createApiAsyncThunk<
    SchemasOrderFamilyPay['/result']['response'],
    string,
    ThunkApi<
        | {
              reason: 'API_ERROR'
              type: string
              title: string
              status: number
              detail: string
              instance: string
              code: string
              orderNo: string
              fallbackMessage: string
          }
        | {
              reason: 'REDIRECT'
              fallbackMessage: string
          }
    >
>('familyPay/initResult', async (orderNo, {rejectWithValue}) => {
    const result = await createWrapper<SchemasOrderFamilyPay['/result']['response']>(
        api.get(ApiUrlInfo.result, {
            params: {orderNo},
            validateStatus: function (status) {
                return status === 302 || status < 300
            },
        }),
    )

    if ('redirectType' in result) {
        return rejectWithValue({
            fallbackMessage: '주문상태가 변경되어 주문완료 페이지 접근이 불가합니다. 주문상세화면으로 이동됩니다.',
            reason: 'REDIRECT',
        })
    }

    return result
})

/** 패밀리 결제 반려 */
export const rejectOrderSheet = createApiAsyncThunk<
    SchemasOrderFamilyPay['/reject']['response'],
    void,
    ThunkApi<{
        type: string
        title: string
        status: number
        detail: string
        instance: string
        defaultMessage: string
        code: string
    }>
>('familyPay/reject', async (_, {dispatch, getState}) => {
    const state = getState()
    const {seqID} = commonSelector(state)
    const {products} = orderSheetProductInfoSelector(state)

    const result = await createWrapper<SchemasOrderFamilyPay['/reject']['response']>(
        api.post(ApiUrlInfo.orderSheetReject, {
            orderNo: seqID,
            productOrderNos: products.map<string>(({id}) => id),
        }),
    )

    if (result.orderStatusType) {
        dispatch(
            systemAlert({
                type: 'ALERT',
                message: '주문상태가 유효하지 않습니다.',
            }),
        )
        dispatch(
            navigateToOrderDetail({
                historyType: 'REPLACE',
                orderNo: seqID ?? '',
            }),
        )
    } else {
        dispatch(
            changeLocation({
                type: 'REPLACE',
                url: `${ROUTER_ORDER_SHEET_FAMILY_PAY.RESULT}/${seqID}`,
            }),
        )
    }
    return result
})

export const getPaymentMethodExists = createAsyncThunk<boolean, void, {rejectValue: string}>(
    'familyPay/getPaymentMethodExists',
    async () => {
        const {existence} = await createWrapper<SchemasOrderFamilyPay['/paymentMethodExists']['response']>(
            api.get('/paymentMethodExists'),
        )

        return existence
    },
)
